import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Color, FieldSize, LIST_PAGES, approvedUnitLabel, approvedUnitOf, approvedUnitsFor, NEW_DOCUMENT_ID, STOCK_COUNT_SCOPES, Size, pageKindLineText, suggestionRowsFor, t,
  type AsyncState, type BannerFact, type IconMeaning, type ReturnableIssue, type StockCountScopeValue, type StringKey, type SuggestionRow, type SuggestionSource, newDocumentTitle,
} from '@bms2/shared';
import { AlertBanner, Button, Dropdown, NumericInput, Select, TextInput, StatusPill } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { DataTable } from '../../components/composites';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/modals/Modal';
import { ModalField } from '../../components/modals/ModalShell';
import { useApprovedUnits } from '../../shell/ApprovedUnits';
import { DocumentEditPage } from './DocumentEditPage';
import { DocumentViewPage, type DocumentLinesDescriptor } from './DocumentViewPage';
import { PAGES_PATH, PICKER_CAP, STOCK_SITES_PATH, apiFetch, post, put, useApi } from './api';
import styles from './StockDocumentPages.module.css';
import { useStatusResolver } from '../../shell/StatusResolver';
import { useAddNew } from './AddNewHost';
import { RefusalText } from './RefusalText';
import { DocumentNotes } from './DocumentNotes';
import { AccountJournalTile } from '../accounting/AccountJournalTile';
import { useDayText } from '../../shell/viewerDates';
import { removeLabel } from '../../components/removeLabel';

/**
 * The three stock documents' screens (piece 22, Stock transfer and stock count;
 * decision 0441). The record layer, the postings and the routes shipped
 * 2026-09-15 with no screen behind them, so the three Inventory lists offered
 * no way to create anything and their rows opened Not Found.
 *
 * 🔨 A transfer and a count carry a table of lines, so each is a page and never
 * a modal (decision 0409). A revaluation is seven fields and no table, so it is
 * a modal on its own list. 🔨 A revaluation posts at creation and a posted count
 * refuses every edit, so neither detail page offers an edit mode.
 *
 * 🔒 Each of the three is drawn by the one document view page (T182, on decision
 * 0612), fed a descriptor: the facts row, the shared line table and the one
 * action bar on the floor. 💰 Each drew its own page module, its own facts row
 * and its own table of three near-identical shapes before this.
 */
export const STOCK_DOCUMENTS_PATH = '/stock-documents';

export interface TransferAnswer {
  id: string;
  transferNumber: string;
  fromSiteId: string;
  fromSiteName: string;
  toSiteId: string;
  toSiteName: string;
  status: string;
  transferredAt: string;
  lines: { id: string; itemId: string; sku: string; itemName: string; quantity: number; unitName: string }[];
}

export interface CountAnswer {
  id: string;
  countNumber: string;
  siteId: string;
  siteName: string;
  status: string;
  // The server sends the `StockCountScope` enum itself, so the label map is total over it.
  scope: StockCountScopeValue;
  startedAt: string;
  postedAt: string | null;
  lines: { id: string; itemId: string; sku: string; itemName: string; expectedQuantity: number; countedQuantity: number | null; varianceNote: string | null; variance: number | null }[];
  /** 🔒 Whether the route would take this viewer's Post (decision 1035); absent from an older reply, and then Post is not drawn. */
  mayPost?: boolean;
}

export interface RevaluationAnswer {
  id: string;
  revaluationNumber: string;
  itemId: string;
  sku: string;
  siteId: string;
  siteName: string;
  quantity: number;
  oldUnitCost: number;
  newUnitCost: number;
  reason: string;
  postedAt: string;
  differenceCents: number;
}

/** One line being written onto a new transfer or picked for a new count. */
interface PickedLine {
  key: string;
  itemId: string | null;
  itemLabel: string;
  quantity: number | null;
  /** 🔒 The unit a transfer line moves in, one of the Item's own family (decision 1034); null is the Item's own unit. */
  unitId?: string | null;
}

/** The Item's own unit as the picker's search answered it: the name the approved list matches and its id. */
interface ItemUnit {
  unit: string;
  unitId: string | null;
}

let nextPick = 0;
const emptyPick = (): PickedLine => {
  nextPick += 1;
  return { key: `pick-${nextPick}`, itemId: null, itemLabel: '', quantity: 1 };
};

/** The remove control names the row it removes, the way the shared grid's does. */
const lineRemoveLabel = (line: PickedLine): string => removeLabel('item.remove', line.itemLabel || line.key, ': ');

/** An Item read as its catalog SKU and its name, the one spelling every stock table uses. */
const itemText = (row: { sku: string; itemName: string }): string => `${row.sku} · ${row.itemName}`;

/** A count's three scopes, each named where the string keys are named rather than spelled from the enum member. */
const SCOPE_LABELS: Readonly<Record<StockCountScopeValue, StringKey>> = {
  FullSite: 'stock.count.scope.fullSite',
  ItemCategory: 'stock.count.scope.itemCategory',
  Spot: 'stock.count.scope.spot',
};

/** The status pill the Page Module's banner carries; the footer is drawn only beside one (piece 12). */
function StatusOf({ documentType, status }: { documentType: string; status: string }) {
  // 🔒 The label and the colour are the seeded rows' for the document's own status enum, through the one resolver (decision 0639).
  const resolveStatus = useStatusResolver();
  return (
    <span data-stock-status={status}>
      <StatusPill entityType={`${documentType}Status`} statusValue={status} resolve={resolveStatus} size={Size.Medium} />
    </span>
  );
}

/** The two pickers every stock screen needs: the sites stock moves between, and the Items it moves. */
function useStockSearches(apiBaseUrl: string) {
  // 🔒 Each searched Item's own unit, which a transfer line's unit control opens on and whose family it offers (decision 1034).
  const itemUnits = useRef(new Map<string, ItemUnit>());
  const search = useCallback(
    (recordType: 'SiteRecord' | 'Item') =>
      async (term: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
        /**
         * 🔒 A site picker on a stock screen offers our own sites alone
         * (decision 0451). It searched every SiteRecord until 2026-09-16, so a
         * customer's site was offered as somewhere to hold, count or move
         * stock — and the row it wrote was a shelf nobody owns.
         */
        const path = recordType === 'SiteRecord' ? STOCK_SITES_PATH : `${PAGES_PATH}/list/${recordType}`;
        // The stock sites route is not the list route and takes no page; every list-route picker names its own cap (T1276).
        const cap = recordType === 'SiteRecord' ? '' : `${PICKER_CAP}&`;
        const response = await apiFetch(`${apiBaseUrl}${path}?${cap}q=${encodeURIComponent(term)}`, { signal });
        if (!response.ok) return [];
        const body = (await response.json()) as { rows: SuggestionSource[] };
        if (recordType === 'Item') {
          for (const row of body.rows) itemUnits.current.set(String(row.id), { unit: String(row.unit ?? ''), unitId: typeof row.unitId === 'string' ? row.unitId : null });
        }
        // 🔨 One mapping per record type, so a picker here cannot invent a row shape of its own.
        return suggestionRowsFor(recordType, body.rows);
      },
    [apiBaseUrl],
  );
  return { siteSearch: search('SiteRecord'), itemSearch: search('Item'), unitOfItem: (itemId: string | null): ItemUnit | null => (itemId ? itemUnits.current.get(itemId) ?? null : null) };
}

/** The line picker both creation screens draw: an Item, a quantity where the document carries one, and a remove. */
function LinePicker({ lines, onChange, itemSearch, quantities, unitOfItem = null }: {
  lines: PickedLine[];
  onChange: (lines: PickedLine[]) => void;
  itemSearch: (term: string, signal: AbortSignal) => Promise<SuggestionRow[]>;
  quantities: boolean;
  /** Where given, each line draws a Unit column offering its Item's own unit family (decision 1034); a count moves no quantity and draws none. */
  unitOfItem?: ((itemId: string | null) => ItemUnit | null) | null;
}) {
  const addNew = useAddNew();
  const approved = useApprovedUnits();
  const patch = (key: string, change: Partial<PickedLine>) => onChange(lines.map((l) => (l.key === key ? { ...l, ...change } : l)));
  /**
   * 🔒 The unit a line moves in (decisions 0439, 1034): the Item's own, and the
   * other approved units of its family, as the priced grid offers them. An Item
   * whose family holds one approved unit, or whose unit nobody approved, reads
   * its unit locked, because there is nothing to choose.
   */
  const renderUnit = (line: PickedLine) => {
    const own = unitOfItem?.(line.itemId) ?? null;
    if (!line.itemId || !own) return <span data-stock-unit="none" />;
    const offered = approvedUnitsFor(approved, { freeText: false, unit: own.unit });
    const standing = approvedUnitOf(approved, own.unit);
    if (offered.length < 2) return <span data-stock-unit="locked">{standing?.unitName ?? own.unit}</span>;
    return (
      <Select size={FieldSize.Column}
        label={t('item.column.unit')}
        labelHidden
        value={line.unitId ?? own.unitId ?? standing?.id ?? ''}
        options={offered.map((unit) => ({ value: unit.id, label: approvedUnitLabel(unit) }))}
        onChange={(unitId) => patch(line.key, { unitId: unitId === own.unitId ? null : unitId })}
        dataAttribute={{ 'data-stock-unit': 'list' }}
      />
    );
  };
  return (
    <div className={styles.scroll} data-stock-lines="true">
      <div className={styles.lineFrame}>
        <DataTable
          columns={[
            { key: 'item', header: t('item.column.item') },
            ...(quantities ? [{ key: 'qty', header: t('item.column.qty') }] : []),
            ...(unitOfItem ? [{ key: 'unit', header: t('item.column.unit') }] : []),
            { key: 'remove', header: t('item.remove'), headerHidden: true, narrow: true },
          ]}
        >
          {lines.map((line) => (
            <tr key={line.key} data-stock-line={line.key}>
              <td>
                <Dropdown size={FieldSize.Column}
                  key={line.itemId ?? line.key}
                  label={t('item.column.item')}
                  labelHidden
                  entityLabel={t('item.entityItem')}
                  search={itemSearch}
                  value={line.itemLabel}
                  // A fresh Item brings its own unit, so a unit chosen against the Item before it drops away (decision 0439).
                  onSelect={(row) => patch(line.key, { itemId: row.id, itemLabel: row.line1, unitId: null })}
                  onAddNew={() => addNew.open({ kind: 'record', recordType: 'Item' }, (row) => patch(line.key, { itemId: row.id, itemLabel: row.line1, unitId: null }))}
                />
              </td>
              {quantities ? (
                <td>
                  <NumericInput size={Size.Small} label={t('item.column.qty')} labelHidden kind="number" value={line.quantity} onChange={(v) => patch(line.key, { quantity: v })} />
                </td>
              ) : null}
              {unitOfItem ? <td>{renderUnit(line)}</td> : null}
              <td>
                <button type="button" className={styles.iconButton} aria-label={lineRemoveLabel(line)} onClick={() => onChange(lines.filter((l) => l.key !== line.key))}>
                  <Icon meaning="remove" size="inline" />
                </button>
              </td>
            </tr>
          ))}
        </DataTable>
      </div>
      <Button size={Size.Small} text={[t('item.addRow')]} actions={[() => onChange([...lines, emptyPick()])]} />
      {addNew.host}
    </div>
  );
}

/**
 * The loading, empty and failed states while the document itself is being
 * read. The panel's render prop is never called here, because the page returns
 * this only while the read has not landed and draws the document itself once
 * it has.
 */
function StillReading<T>({ state, onRetry }: { state: AsyncState<T>; onRetry: () => void }) {
  return <AsyncStatePanel state={state} emptyAction={t('stock.notFound')} onRetry={onRetry}>{() => null}</AsyncStatePanel>;
}

/**
 * 🔒 What every stock document's page is fed (T182). The three differ in their
 * facts, their lines and the moves their footer offers, and in nothing else:
 * none of them prints a sheet, so each opens on Details, and none of them moves
 * money, so neither line table draws an amount column.
 */
function StockDocumentPage({ documentType, recordId, icon, number, subtitle, status, facts, lines, footer, footerAttributes, error, onDismissError, pageAttributes, listRoute, apiBaseUrl = '', children }: {
  documentType: 'StockTransfer' | 'StockCount';
  /** The document's own id, which its notes are tagged to (decision 0951). */
  recordId: string;
  apiBaseUrl?: string;
  icon: IconMeaning;
  number: string;
  subtitle: string;
  status: string | null;
  facts: readonly BannerFact[];
  lines?: DocumentLinesDescriptor | null;
  footer?: ReactNode;
  footerAttributes?: Record<string, string>;
  error: string | null;
  onDismissError: () => void;
  pageAttributes: Record<string, string>;
  listRoute: string;
  children?: ReactNode;
}) {
  // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
  const notesPanel = <DocumentNotes recordType={documentType} recordId={recordId} recordLabel={number} apiBaseUrl={apiBaseUrl} />;
  // 🔒 A count posts its costed variance, a transfer posts nothing (decision 0195): the count's one Account Journal tile is placed in the Details view and handed to the rail's Journal tab (T3404; decision 1662).
  const journalPanel = documentType === 'StockCount' ? <AccountJournalTile sourceDocumentType="StockCount" sourceDocumentId={recordId} apiBaseUrl={apiBaseUrl} /> : null;
  return (
    <DocumentViewPage
      notes={notesPanel}
      {...(journalPanel === null ? {} : { journal: journalPanel })}
      documentType={documentType}
      icon={icon}
      title={number}
      subtitle={subtitle}
      information={facts}
      {...(status === null ? {} : { status: <StatusOf documentType={documentType} status={status} /> })}
      {...(footer === undefined ? {} : { footer })}
      {...(footerAttributes === undefined ? {} : { footerAttributes })}
      pageAttributes={pageAttributes}
      listRoute={listRoute}
      lines={lines ?? null}
      /* 🔨 None of the three prints, so each opens on Details and says it has no sheet rather than drawing an empty frame. */
      sheet={null}
      notices={error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: onDismissError }} /> : null}
      /* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */
      details={
        <>
          {/* 🔒 A count posts its costed variance, a transfer posts nothing (decision 0195); the count shows what it posted (decision 1075). */}
          {journalPanel}
          {notesPanel}
        </>
      }
      {...(children === undefined ? {} : { children })}
    />
  );
}

// ---- Stock transfer ----

/** 🔨 A transfer moves stock between two sites and posts nothing to the ledger; the movement pair is written when it is received. */
export function StockTransferPage({ transferId, apiBaseUrl = '' }: { transferId: string; apiBaseUrl?: string }) {
  const dayText = useDayText();
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  // 🔨 An instant reads as a date in the viewer's own zone, never sliced off the UTC string (decision 0261).
  const isNew = transferId === NEW_DOCUMENT_ID;
  const { siteSearch, itemSearch, unitOfItem } = useStockSearches(apiBaseUrl);
  const read = useApi<TransferAnswer>(isNew ? null : `${STOCK_DOCUMENTS_PATH}/transfers/${transferId}`, apiBaseUrl);
  const [from, setFrom] = useState<{ id: string; label: string } | null>(null);
  const [to, setTo] = useState<{ id: string; label: string } | null>(null);
  const [lines, setLines] = useState<PickedLine[]>([emptyPick()]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (): Promise<void> => {
    const picked = lines.filter((l) => l.itemId);
    if (!from) return setError(t('stock.transfer.needsFrom'));
    if (!to) return setError(t('stock.transfer.needsTo'));
    if (from.id === to.id) return setError(t('stock.transfer.sameSite'));
    if (picked.length === 0) return setError(t('stock.transfer.needsLine'));
    setBusy(true);
    const saved = await post<{ id: string }>(`${STOCK_DOCUMENTS_PATH}/transfers`, {
      fromSiteId: from.id, toSiteId: to.id,
      // 🔒 A unit chosen from the Item's family goes with the line; none sent is the Item's own, which the service resolves (decision 1034).
      lines: picked.map((l) => ({ itemId: l.itemId, quantity: l.quantity ?? 1, ...(l.unitId ? { unitId: l.unitId } : {}) })),
    }, apiBaseUrl);
    setBusy(false);
    if (!saved.ok) return setError(saved.message);
    navigate(`${LIST_PAGES.StockTransfer.route}/${saved.data.id}`);
  };

  if (isNew) {
    return (
      <DocumentEditPage
        title={newDocumentTitle('StockTransfer')}
        subtitle={t('stock.transfer.postsNothing')}
        outwardLabel={t('stock.transfer.save')}
        busy={busy}
        error={error}
        onDismissError={() => setError(null)}
        onSaveAndSend={() => void save()}
        onCancel={() => navigate(LIST_PAGES.StockTransfer.route)}
        header={
          <>
            <ModalField label={t('stock.transfer.from')}>
              <span data-stock-site="from">
                <Dropdown size={Size.Medium} label={t('stock.transfer.from')} labelHidden entityLabel={t('stock.entitySite')} search={siteSearch} value={from?.label ?? null} onSelect={(row) => setFrom({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'site' }, (row) => setFrom({ id: row.id, label: row.line1 }))} required />
              </span>
            </ModalField>
            <ModalField label={t('stock.transfer.to')}>
              <span data-stock-site="to">
                <Dropdown size={Size.Medium} label={t('stock.transfer.to')} labelHidden entityLabel={t('stock.entitySite')} search={siteSearch} value={to?.label ?? null} onSelect={(row) => setTo({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'site' }, (row) => setTo({ id: row.id, label: row.line1 }))} required />
              </span>
            </ModalField>
          </>
        }
        lines={<LinePicker lines={lines} onChange={setLines} itemSearch={itemSearch} quantities unitOfItem={unitOfItem} />}
      />
    );
  }

  if (read.state.kind !== 'ready') return <StillReading state={read.state} onRetry={read.reload} />;
  const row = read.state.data;
  const move = async (to: 'InTransit' | 'Received' | 'Cancelled'): Promise<void> => {
    setBusy(true);
    const moved = await post(`${STOCK_DOCUMENTS_PATH}/transfers/${row.id}/move`, { to }, apiBaseUrl);
    setBusy(false);
    if (!moved.ok) return setError(moved.message);
    read.reload();
  };
  // Draft despatches, InTransit arrives, and either cancels while nothing has moved.
  const onward = row.status === 'Draft' ? ('InTransit' as const) : row.status === 'InTransit' ? ('Received' as const) : null;

  /** 🔒 The items moved, through the one read-only line table with no money column (T182). */
  const lineTable: DocumentLinesDescriptor = {
    title: t('stock.transfer.linesTitle'),
    rowAttribute: 'data-stock-line',
    blockAttribute: 'data-stock-lines',
    columns: [t('item.column.item'), t('item.column.qty'), t('item.column.unit')],
    money: false,
    empty: t('stock.transfer.noLines'),
    rows: row.lines.map((line) => ({ id: line.id, label: itemText(line), cells: [String(line.quantity), line.unitName] })),
  };

  return (
    <>
    <StockDocumentPage
      documentType="StockTransfer"
      recordId={row.id}
      apiBaseUrl={apiBaseUrl}
      icon="stockTransfer"
      number={row.transferNumber}
      subtitle={pageKindLineText({ page: 'document', documentType: 'StockTransfer' })}
      status={row.status}
      facts={[
        { label: t('stock.transfer.from'), value: row.fromSiteName },
        { label: t('stock.transfer.to'), value: row.toSiteName },
        { label: t('stock.raised'), value: dayText(row.transferredAt) },
        { label: t('stock.postsNothingLabel'), value: t('stock.transfer.postsNothing') },
      ]}
      lines={lineTable}
      {...(onward
        ? {
            footer: (
              <span data-stock-action={onward}>
                <Button size={Size.Medium} color={Color.Navy} text={[onward === 'InTransit' ? t('stock.transfer.despatch') : t('stock.transfer.receive')]} actions={[() => void move(onward)]} disabled={busy} />
                <Button size={Size.Medium} color={Color.Inactive} text={[t('stock.cancel')]} actions={[() => void move('Cancelled')]} disabled={busy} />
              </span>
            ),
          }
        : {})}
      footerAttributes={{ 'data-stock-footer': row.status }}
      error={error}
      onDismissError={() => setError(null)}
      pageAttributes={{ 'data-stock-transfer': row.id, 'data-stock-status': row.status }}
      listRoute={LIST_PAGES.StockTransfer.route}
    />
    {addNew.host}
    </>
  );
}

// ---- Stock count ----

/** 🔨 A count corrects the on-hand number: each line snapshots what was expected when it was created, and posting writes one Adjustment per varying line. */
export function StockCountPage({ countId, apiBaseUrl = '' }: { countId: string; apiBaseUrl?: string }) {
  const dayText = useDayText();
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  // 🔨 An instant reads as a date in the viewer's own zone, never sliced off the UTC string (decision 0261).
  const isNew = countId === NEW_DOCUMENT_ID;
  const { siteSearch, itemSearch } = useStockSearches(apiBaseUrl);
  const read = useApi<CountAnswer>(isNew ? null : `${STOCK_DOCUMENTS_PATH}/counts/${countId}`, apiBaseUrl);
  const [site, setSite] = useState<{ id: string; label: string } | null>(null);
  const [scope, setScope] = useState<StockCountScopeValue>('FullSite');
  // 🔒 A new count's picker opens on one blank line, the way the Work Order's editor does (decision 0861, the owner's T141).
  const [lines, setLines] = useState<PickedLine[]>(() => [emptyPick()]);
  const [counted, setCounted] = useState<Record<string, { quantity: number | null; note: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async (): Promise<void> => {
    if (!site) return setError(t('stock.count.needsSite'));
    setBusy(true);
    const itemIds = lines.filter((l) => l.itemId).map((l) => l.itemId as string);
    const saved = await post<{ id: string }>(`${STOCK_DOCUMENTS_PATH}/counts`, { siteId: site.id, scope, ...(itemIds.length > 0 ? { itemIds } : {}) }, apiBaseUrl);
    setBusy(false);
    if (!saved.ok) return setError(saved.message);
    navigate(`${LIST_PAGES.StockCount.route}/${saved.data.id}`);
  };

  if (isNew) {
    return (
      <DocumentEditPage
        title={newDocumentTitle('StockCount')}
        subtitle={t('stock.count.newHint')}
        outwardLabel={t('stock.count.save')}
        busy={busy}
        error={error}
        onDismissError={() => setError(null)}
        onSaveAndSend={() => void create()}
        onCancel={() => navigate(LIST_PAGES.StockCount.route)}
        header={
          <>
            <ModalField label={t('stock.count.site')}>
              <span data-stock-site="count">
                <Dropdown size={Size.Medium} label={t('stock.count.site')} labelHidden entityLabel={t('stock.entitySite')} search={siteSearch} value={site?.label ?? null} onSelect={(row) => setSite({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'site' }, (row) => setSite({ id: row.id, label: row.line1 }))} required />
              </span>
            </ModalField>
            <Select size={Size.Large}
              label={t('stock.count.scope')}
              value={scope}
              onChange={(v) => setScope(v as StockCountScopeValue)}
              options={STOCK_COUNT_SCOPES.map((value) => ({ value, label: t(SCOPE_LABELS[value]) }))}
              dataAttribute={{ 'data-stock-scope': 'true' }}
              required
            />
          </>
        }
        lines={
          <>
            <p className={styles.hint} data-stock-count-hint="true">{t('stock.count.itemsHint')}</p>
            <LinePicker lines={lines} onChange={setLines} itemSearch={itemSearch} quantities={false} />
          </>
        }
      />
    );
  }

  if (read.state.kind !== 'ready') return <StillReading state={read.state} onRetry={read.reload} />;
  const row = read.state.data;
  const draftOf = (lineId: string, expected: { countedQuantity: number | null; varianceNote: string | null }) =>
    counted[lineId] ?? { quantity: expected.countedQuantity, note: expected.varianceNote ?? '' };
  // 🔨 A posted or cancelled count refuses every edit; a re-count is a new count.
  const open = row.status !== 'Posted' && row.status !== 'Cancelled';

  const saveLines = async (): Promise<void> => {
    setBusy(true);
    const body = Object.entries(counted).map(([lineId, draft]) => ({ lineId, countedQuantity: draft.quantity, varianceNote: draft.note.trim() || null }));
    const saved = await put(`${STOCK_DOCUMENTS_PATH}/counts/${row.id}/lines`, { lines: body }, apiBaseUrl);
    setBusy(false);
    if (!saved.ok) return setError(saved.message);
    setCounted({});
    read.reload();
  };
  const act = async (path: string, body: unknown): Promise<void> => {
    setBusy(true);
    const done = await post(`${STOCK_DOCUMENTS_PATH}/counts/${row.id}/${path}`, body, apiBaseUrl);
    setBusy(false);
    if (!done.ok) return setError(done.message);
    read.reload();
  };

  /**
   * 🔒 The items counted, through the one read-only line table with no money
   * column (T182). 🔨 A count is read and written on the same table, so the
   * counted quantity and the reason it differs are cells carrying a field while
   * the count is open and their own text once it is closed.
   */
  const lineTable: DocumentLinesDescriptor = {
    title: t('stock.count.linesTitle'),
    rowAttribute: 'data-stock-line',
    blockAttribute: 'data-stock-lines',
    columns: [t('item.column.item'), t('stock.count.expected'), t('stock.count.counted'), t('stock.count.variance'), t('stock.count.note')],
    money: false,
    empty: t('stock.count.noLines'),
    // 🔒 While the count is open, the sentence beside its counted quantities: the count on the shelf, not the change (decision 1142).
    ...(open ? { hint: t('stock.count.countedHint') } : {}),
    rows: row.lines.map((line) => {
      const draft = draftOf(line.id, line);
      return {
        id: line.id,
        label: itemText(line),
        attributes: { 'data-stock-variance': String(line.variance ?? '') },
        cells: [
          String(line.expectedQuantity),
          open
            ? { text: '', node: <NumericInput size={Size.Small} label={t('stock.count.counted')} labelHidden kind="number" value={draft.quantity} onChange={(v) => setCounted((held) => ({ ...held, [line.id]: { ...draftOf(line.id, line), quantity: v } }))} /> }
            : String(line.countedQuantity ?? ''),
          String(line.variance ?? ''),
          open
            ? { text: '', node: <TextInput size={FieldSize.Column} label={t('stock.count.note')} labelHidden value={draft.note} onChange={(v) => setCounted((held) => ({ ...held, [line.id]: { ...draftOf(line.id, line), note: v } }))} /> }
            : line.varianceNote ?? '',
        ],
      };
    }),
  };

  return (
    <>
    <StockDocumentPage
      documentType="StockCount"
      recordId={row.id}
      apiBaseUrl={apiBaseUrl}
      icon="stockCount"
      number={row.countNumber}
      subtitle={pageKindLineText({ page: 'document', documentType: 'StockCount' })}
      status={row.status}
      facts={[
        { label: t('stock.count.site'), value: row.siteName },
        { label: t('stock.count.scope'), value: t(SCOPE_LABELS[row.scope]) },
        { label: t('stock.count.started'), value: dayText(row.startedAt) },
        { label: t('stock.count.posted'), value: row.postedAt ? dayText(row.postedAt) : '' },
      ]}
      lines={lineTable}
      {...(open
        ? {
            footer: (
              <span data-stock-action="count">
                <Button size={Size.Medium} color={Color.Navy} text={[t('stock.count.saveCounts')]} actions={[() => void saveLines()]} disabled={busy || Object.keys(counted).length === 0} />
                {row.status === 'Counting' ? <Button size={Size.Medium} color={Color.Inactive} text={[t('stock.count.review')]} actions={[() => void act('move', { to: 'Review' })]} disabled={busy} /> : null}
                {/* 🔒 Post is not drawn for a viewer the route would refuse, rather than refused after the press (decision 1035). */}
                {row.status !== 'Draft' && row.mayPost === true ? <Button size={Size.Medium} color={Color.Navy} text={[t('stock.count.post')]} actions={[() => void act('post', {})]} disabled={busy} /> : null}
                <Button size={Size.Medium} color={Color.Inactive} text={[t('stock.cancel')]} actions={[() => void act('move', { to: 'Cancelled' })]} disabled={busy} />
              </span>
            ),
          }
        : {})}
      footerAttributes={{ 'data-stock-footer': row.status }}
      error={error}
      onDismissError={() => setError(null)}
      pageAttributes={{ 'data-stock-count': row.id, 'data-stock-status': row.status }}
      listRoute={LIST_PAGES.StockCount.route}
    />
    {addNew.host}
    </>
  );
}

// ---- Stock revaluation ----

/** 🔨 A revaluation posts at creation and moves value, never quantity, so its page is a reading of what was posted. */
export function StockRevaluationPage({ revaluationId, apiBaseUrl = '' }: { revaluationId: string; apiBaseUrl?: string }) {
  const dayText = useDayText();
  // 🔨 An instant reads as a date in the viewer's own zone, never sliced off the UTC string (decision 0261).
  const read = useApi<RevaluationAnswer>(`${STOCK_DOCUMENTS_PATH}/revaluations/${revaluationId}`, apiBaseUrl);
  if (read.state.kind !== 'ready') return <StillReading state={read.state} onRetry={read.reload} />;
  const row = read.state.data;
  // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
  const notesPanel = <DocumentNotes recordType="StockRevaluation" recordId={row.id} recordLabel={row.revaluationNumber} apiBaseUrl={apiBaseUrl} />;
  // 🔒 What the revaluation posted between 1200 and 5300 (decision 1075), placed in the Details view and handed to the rail's Journal tab (T3404; decision 1662).
  const journalPanel = <AccountJournalTile sourceDocumentType="StockRevaluation" sourceDocumentId={row.id} apiBaseUrl={apiBaseUrl} />;
  return (
    <DocumentViewPage
      notes={notesPanel}
      journal={journalPanel}
      documentType="StockRevaluation"
      icon="stockRevaluation"
      title={row.revaluationNumber}
      subtitle={pageKindLineText({ page: 'document', documentType: 'StockRevaluation' })}
      information={[
        { label: t('stock.revaluation.item'), value: `${row.sku} · ${row.siteName}` },
        { label: t('stock.revaluation.quantity'), value: String(row.quantity) },
        { label: t('stock.revaluation.was'), value: String(row.oldUnitCost) },
        { label: t('stock.revaluation.now'), value: String(row.newUnitCost) },
        { label: t('stock.count.posted'), value: dayText(row.postedAt) },
      ]}
      pageAttributes={{ 'data-stock-revaluation': row.id }}
      listRoute={LIST_PAGES.StockRevaluation.route}
      /* 🔨 A revaluation carries no lines at all: it reprices what is already on the shelf and moves no quantity. */
      lines={null}
      sheet={null}
      details={
        <>
          <p className={styles.hint} data-stock-reason="true">{row.reason}</p>
          <p className={styles.hint} data-stock-difference={String(row.differenceCents)}>{t('stock.revaluation.quantityUnchanged')}</p>
          {journalPanel}
          {/* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */}
          {notesPanel}
        </>
      }
    />
  );
}

/**
 * New Stock Revaluation: seven fields and no table, so it is a modal rather
 * than a page (decision 0409). 🔨 The reason is required, because a change in
 * the value of stock on hand with nobody's explanation behind it is the one
 * number that reconciles to money and cannot be re-derived.
 */
export function NewRevaluationModal({ open, apiBaseUrl = '', onCancel, onPosted }: { open: boolean; apiBaseUrl?: string; onCancel: () => void; onPosted: (id: string) => void }) {
  const addNew = useAddNew(apiBaseUrl);
  const { siteSearch, itemSearch } = useStockSearches(apiBaseUrl);
  const [item, setItem] = useState<{ id: string; label: string } | null>(null);
  const [site, setSite] = useState<{ id: string; label: string } | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [oldCost, setOldCost] = useState<number | null>(null);
  const [newCost, setNewCost] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    if (!item) return setError(t('stock.revaluation.needsItem'));
    if (!site) return setError(t('stock.revaluation.needsSite'));
    if (!reason.trim()) return setError(t('stock.revaluation.needsReason'));
    setBusy(true);
    const saved = await post<{ id: string }>(`${STOCK_DOCUMENTS_PATH}/revaluations`, {
      itemId: item.id, siteId: site.id, quantity, newUnitCost: newCost, reason: reason.trim(),
      // 🔨 An old cost nobody typed is read from the stock on hand at the site rather than guessed here.
      ...(oldCost === null ? {} : { oldUnitCost: oldCost }),
    }, apiBaseUrl);
    setBusy(false);
    if (!saved.ok) return setError(saved.message);
    onPosted(saved.data.id);
  };

  return (
    <Modal
      open={open}
      title={newDocumentTitle('StockRevaluation')}
      subtitle={t('stock.revaluation.newHint')}
      onCancel={onCancel}
      dataModal="newRevaluation"
      actions={[
        { kind: 'primary', label: busy ? t('stock.revaluation.posting') : t('stock.revaluation.post'), onAct: () => submit() },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <ModalField label={t('stock.revaluation.item')}>
        <span data-stock-field="item">
          <Dropdown size={Size.Medium} label={t('stock.revaluation.item')} labelHidden entityLabel={t('item.entityItem')} search={itemSearch} value={item?.label ?? null} onSelect={(row) => setItem({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'Item' }, (row) => setItem({ id: row.id, label: row.line1 }))} required />
        </span>
      </ModalField>
      <ModalField label={t('stock.revaluation.site')}>
        <span data-stock-field="site">
          <Dropdown size={Size.Medium} label={t('stock.revaluation.site')} labelHidden entityLabel={t('stock.entitySite')} search={siteSearch} value={site?.label ?? null} onSelect={(row) => setSite({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'site' }, (row) => setSite({ id: row.id, label: row.line1 }))} required />
        </span>
      </ModalField>
      <NumericInput size={Size.Small} label={t('stock.revaluation.quantity')} kind="number" value={quantity} onChange={setQuantity} required />
      <NumericInput size={Size.Small} label={t('stock.revaluation.wasOptional')} kind="number" value={oldCost} onChange={setOldCost} hint={t('stock.revaluation.wasHint')} />
      <NumericInput size={Size.Small} label={t('stock.revaluation.now')} kind="number" value={newCost} onChange={setNewCost} required />
      <TextInput size={Size.Large} label={t('stock.revaluation.reason')} value={reason} onChange={setReason} required name="revaluationReason" />
      {addNew.host}
    </Modal>
  );
}

/** What Issue Stock answers (decision 1142): the movement and entry it wrote, and what the shelf holds after it. */
export interface IssueAnswer {
  movementId: string;
  entryId: string | null;
  sku: string;
  siteName: string;
  quantity: number;
  onHandAfter: number;
}

/**
 * Issue Stock (piece 23; decision 1142; T445): a part picked off a shelf for a
 * customer or a Deal with no job behind it. 🔒 It lowers the site's stock and
 * posts the cost as a shipment does, 5000 against 1200. 🔨 The server refuses
 * a quantity above the site's on-hand in a sentence, and the window shows it.
 * 🔨 It holds its fields in its own state, so the caller unmounts it when it
 * closes rather than hiding it, as the revaluation's window is.
 */
export function IssueStockModal({ apiBaseUrl = '', item: fixedItem = null, onCancel, onIssued }: { apiBaseUrl?: string; item?: { id: string; label: string } | null; onCancel: () => void; onIssued: (answer: IssueAnswer) => void }) {
  const addNew = useAddNew(apiBaseUrl);
  const { siteSearch, itemSearch } = useStockSearches(apiBaseUrl);
  const dealSearch = useCallback(
    async (term: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
      const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/Deal?${PICKER_CAP}&q=${encodeURIComponent(term)}`, { signal });
      if (!response.ok) return [];
      return suggestionRowsFor('Deal', ((await response.json()) as { rows: SuggestionSource[] }).rows);
    },
    [apiBaseUrl],
  );
  const [item, setItem] = useState<{ id: string; label: string } | null>(fixedItem);
  const [site, setSite] = useState<{ id: string; label: string } | null>(null);
  const [deal, setDeal] = useState<{ id: string; label: string } | null>(null);
  const [quantity, setQuantity] = useState<number | null>(1);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<ReactNode>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    if (!item) return setError(t('stock.issue.needsItem'));
    if (!site) return setError(t('stock.issue.needsSite'));
    if (quantity === null || !(quantity > 0)) return setError(t('stock.issue.needsQuantity'));
    if (!reason.trim()) return setError(t('stock.issue.needsReason'));
    setBusy(true);
    const issued = await post<IssueAnswer>(`${STOCK_DOCUMENTS_PATH}/issues`, { itemId: item.id, siteId: site.id, quantity, reason: reason.trim(), dealId: deal?.id ?? null }, apiBaseUrl);
    setBusy(false);
    // 🔒 The server's own sentence, the refusal above the shelf among them, is what the window shows.
    if (!issued.ok) return setError(<RefusalText message={issued.message} body={issued.body} />);
    onIssued(issued.data);
  };

  return (
    <Modal
      open
      title={t('stock.issue.title')}
      subtitle={t('stock.issue.hint')}
      onCancel={onCancel}
      dataModal="issueStock"
      actions={[
        { kind: 'primary', label: busy ? t('stock.issue.posting') : t('stock.issue.post'), onAct: () => submit() },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {fixedItem ? null : (
        <ModalField label={t('stock.issue.item')}>
          <span data-stock-field="item">
            <Dropdown size={Size.Medium} label={t('stock.issue.item')} labelHidden entityLabel={t('item.entityItem')} search={itemSearch} value={item?.label ?? null} onSelect={(row) => setItem({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'Item' }, (row) => setItem({ id: row.id, label: row.line1 }))} required />
          </span>
        </ModalField>
      )}
      <ModalField label={t('stock.issue.site')}>
        <span data-stock-field="site">
          <Dropdown size={Size.Medium} label={t('stock.issue.site')} labelHidden entityLabel={t('stock.entitySite')} search={siteSearch} value={site?.label ?? null} onSelect={(row) => setSite({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'site' }, (row) => setSite({ id: row.id, label: row.line1 }))} required />
        </span>
      </ModalField>
      <NumericInput size={Size.Small} label={t('stock.issue.quantity')} kind="number" value={quantity} onChange={setQuantity} required />
      <TextInput size={Size.Large} label={t('stock.issue.reason')} value={reason} onChange={setReason} required name="issueReason" />
      <ModalField label={t('stock.issue.deal')}>
        <span data-stock-field="deal">
          <Dropdown size={Size.Medium} label={t('stock.issue.deal')} labelHidden entityLabel={t('stock.issue.dealEntity')} search={dealSearch} value={deal?.label ?? null} onSelect={(row) => setDeal({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'Deal' }, (row) => setDeal({ id: row.id, label: row.line1 }))} />
        </span>
      </ModalField>
      {addNew.host}
    </Modal>
  );
}

/** What Return Stock answers (decision 1609): the movement and entry it wrote, what the issue still has out, and the shelf after it. */
export interface ReturnAnswer {
  movementId: string;
  entryId: string | null;
  sku: string;
  siteName: string;
  quantity: number;
  stillOut: number;
  onHandAfter: number;
}

/**
 * Return Stock (piece 23; decision 1609): part of an issue put back on the
 * shelf it left. 🔒 The person names the item and the site, then picks the
 * issue the parts came from among those with parts still out; the quantity
 * opens on what that issue still has out. 🔒 The parts come back at the cost
 * the issue left at, and the issue's entry is reversed for them. 🔨 The server
 * refuses more than the issue still has out, in a sentence, and the window
 * shows it. 🔨 It holds its fields in its own state, so the caller unmounts it
 * when it closes rather than hiding it, as Issue Stock's window is.
 */
export function ReturnStockModal({ apiBaseUrl = '', onCancel, onReturned }: { apiBaseUrl?: string; onCancel: () => void; onReturned: (answer: ReturnAnswer) => void }) {
  const addNew = useAddNew(apiBaseUrl);
  const { siteSearch, itemSearch } = useStockSearches(apiBaseUrl);
  const dayText = useDayText();
  const [item, setItem] = useState<{ id: string; label: string } | null>(null);
  const [site, setSite] = useState<{ id: string; label: string } | null>(null);
  const issuesPath = item && site ? `${STOCK_DOCUMENTS_PATH}/issues?itemId=${encodeURIComponent(item.id)}&siteId=${encodeURIComponent(site.id)}` : null;
  const issuesRead = useApi<{ rows: ReturnableIssue[] }>(issuesPath, apiBaseUrl);
  const issues = issuesRead.state.kind === 'ready' ? issuesRead.state.data.rows : [];
  // 🔒 A new item or site lists its own issues and opens on the newest with parts out, its quantity what it still has out.
  // The person's own pick and quantity stand while the item and the site do, and are read off the list otherwise.
  const [choice, setChoice] = useState<{ path: string | null; issueId: string; quantity: number | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const remainingOf = (movementId: string) => issues.find((row) => row.movementId === movementId)?.remaining ?? null;
  const standing = choice && choice.path === issuesPath ? choice : null;
  const issueId = standing?.issueId ?? issues[0]?.movementId ?? '';
  const quantity = standing ? standing.quantity : remainingOf(issueId);
  const chosen = issues.find((row) => row.movementId === issueId) ?? null;
  const pickIssue = (movementId: string) => setChoice({ path: issuesPath, issueId: movementId, quantity: remainingOf(movementId) });
  const setQuantity = (value: number | null) => setChoice({ path: issuesPath, issueId, quantity: value });

  const submit = async (): Promise<void> => {
    if (!item) return setError(t('stock.return.needsItem'));
    if (!site) return setError(t('stock.return.needsSite'));
    if (!chosen) return setError(t('stock.return.needsIssue'));
    if (quantity === null || !(quantity > 0)) return setError(t('stock.return.needsQuantity'));
    setBusy(true);
    const returned = await post<ReturnAnswer>(`${STOCK_DOCUMENTS_PATH}/issues/${encodeURIComponent(chosen.movementId)}/returns`, { quantity }, apiBaseUrl);
    setBusy(false);
    // 🔒 The server's own sentence, the refusal above what the issue still has out among them, is what the window shows.
    if (!returned.ok) return setError(returned.message);
    onReturned(returned.data);
  };

  const issueLabel = (row: ReturnableIssue) => `${dayText(row.issuedAt)} · ${row.remaining} / ${row.quantity} ${t('stock.return.stillOut')}${row.reason ? ` · ${row.reason}` : ''}`;
  const issueHint = !issuesPath ? t('stock.return.issueFirst') : issuesRead.state.kind === 'loading' || issues.length > 0 ? null : t('stock.return.issueNone');

  return (
    <Modal
      open
      title={t('stock.return.title')}
      subtitle={t('stock.return.hint')}
      onCancel={onCancel}
      dataModal="returnStock"
      actions={[
        { kind: 'primary', label: busy ? t('stock.return.posting') : t('stock.return.post'), onAct: () => submit() },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <ModalField label={t('stock.return.item')}>
        <span data-stock-field="item">
          <Dropdown size={Size.Medium} label={t('stock.return.item')} labelHidden entityLabel={t('item.entityItem')} search={itemSearch} value={item?.label ?? null} onSelect={(row) => setItem({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'Item' }, (row) => setItem({ id: row.id, label: row.line1 }))} required />
        </span>
      </ModalField>
      <ModalField label={t('stock.return.site')}>
        <span data-stock-field="site">
          <Dropdown size={Size.Medium} label={t('stock.return.site')} labelHidden entityLabel={t('stock.entitySite')} search={siteSearch} value={site?.label ?? null} onSelect={(row) => setSite({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'site' }, (row) => setSite({ id: row.id, label: row.line1 }))} required />
        </span>
      </ModalField>
      <Select
        label={t('stock.return.issue')}
        value={issueId}
        onChange={pickIssue}
        options={issues.map((row) => ({ value: row.movementId, label: issueLabel(row) }))}
        placeholder={t('stock.return.issuePlaceholder')}
        disabled={issues.length === 0}
        hint={issueHint}
        required
        size={Size.Large}
        dataAttribute={{ 'data-stock-field': 'issue' }}
      />
      <NumericInput size={Size.Small} label={t('stock.return.quantity')} kind="number" value={quantity} onChange={setQuantity} required name="returnQuantity" />
      {addNew.host}
    </Modal>
  );
}
